import { useEffect, useState } from 'react';
import { CourseLevel, CourseTrackType, SubscriptionPlans } from '@/constants';
import CheckBoxField from '@/shared/components/form/CheckBox';
import DropZoneField from '@/shared/components/form/dropzones/DropZone';
import { MaterialFormOption, MaterialTextField } from '@/shared/components/form/MaterialFormElements';

interface Props {
    initialPayload: Record<string, unknown>;
    onChange: (payload: Record<string, unknown>) => void;
}

/**
 * Shape mirrors CourseTargetHandler.validatePayload. We deliberately exclude
 * `lessons` and `phases` (those are course-structure changes, not the kind of
 * marketing rotation this scheduler is for).
 */
const CoursePayloadForm = ({ initialPayload, onChange }: Props) => {
    const [title, setTitle] = useState((initialPayload.title as string) ?? '');
    const [subtitle, setSubtitle] = useState((initialPayload.subtitle as string) ?? '');
    const [description, setDescription] = useState((initialPayload.description as string) ?? '');
    const [instructor, setInstructor] = useState((initialPayload.instructor as string) ?? '');
    const [trackType, setTrackType] = useState<CourseTrackType | ''>((initialPayload.trackType as CourseTrackType) ?? '');
    const [level, setLevel] = useState<CourseLevel | ''>((initialPayload.level as CourseLevel) ?? '');
    const [subscriptionPlan, setSubscriptionPlan] = useState<SubscriptionPlans | ''>(
        (initialPayload.subscriptionPlan as SubscriptionPlans) ?? '',
    );
    const [isNew, setIsNew] = useState(!!initialPayload.isNew);
    const [isFeatured, setIsFeatured] = useState(!!initialPayload.isFeatured);
    const [isFlagship, setIsFlagship] = useState(!!initialPayload.isFlagship);
    const [imagePath, setImagePath] = useState((initialPayload.imagePath as string) ?? '');
    const [coverGradient, setCoverGradient] = useState((initialPayload.coverGradient as string) ?? '');
    const [orderIndex, setOrderIndex] = useState<number | ''>(
        typeof initialPayload.orderIndex === 'number' ? (initialPayload.orderIndex as number) : '',
    );

    // Track which boolean toggles the admin has actually interacted with — so
    // unchecking a flag is emitted as `false` (not silently dropped) while a
    // flag the admin never touched stays absent from the payload (lets the
    // target keep its current value).
    const [dirtyFlags, setDirtyFlags] = useState<{ isNew?: boolean; isFeatured?: boolean; isFlagship?: boolean }>({});

    // `initialPayload` and `onChange` are deliberately omitted from the deps:
    // `initialPayload` is a stable memoized prop from the parent and is only
    // used to seed the initial state, never re-read; `onChange` is the parent's
    // setState which has stable identity. Keeping them in deps caused unwanted
    // re-fires of the effect each render.
    useEffect(() => {
        const payload: Record<string, unknown> = {};
        if (title) payload.title = title;
        if (subtitle) payload.subtitle = subtitle;
        if (description) payload.description = description;
        if (instructor) payload.instructor = instructor;
        if (trackType) payload.trackType = trackType;
        if (level) payload.level = level;
        if (subscriptionPlan) payload.subscriptionPlan = subscriptionPlan;
        if (dirtyFlags.isNew || initialPayload.isNew !== undefined) payload.isNew = isNew;
        if (dirtyFlags.isFeatured || initialPayload.isFeatured !== undefined) payload.isFeatured = isFeatured;
        if (dirtyFlags.isFlagship || initialPayload.isFlagship !== undefined) payload.isFlagship = isFlagship;
        if (imagePath) payload.imagePath = imagePath;
        if (coverGradient) payload.coverGradient = coverGradient;
        if (orderIndex !== '' && typeof orderIndex === 'number') payload.orderIndex = orderIndex;
        onChange(payload);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, subtitle, description, instructor, trackType, level, subscriptionPlan, isNew, isFeatured, isFlagship, imagePath, coverGradient, orderIndex, dirtyFlags]);

    return (
        <div>
            <MaterialTextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <MaterialTextField label="Subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
            <MaterialTextField label="Description" multiline value={description} onChange={(e) => setDescription(e.target.value)} />
            <MaterialTextField label="Instructor" value={instructor} onChange={(e) => setInstructor(e.target.value)} />
            <MaterialTextField
                name="select"
                select
                label="Track Type (optional)"
                value={trackType}
                onChange={(e) => setTrackType(e.target.value as CourseTrackType | '')}
            >
                <MaterialFormOption value="">— Keep —</MaterialFormOption>
                <MaterialFormOption value={CourseTrackType.Trader}>Trader</MaterialFormOption>
                <MaterialFormOption value={CourseTrackType.General}>General</MaterialFormOption>
            </MaterialTextField>
            <MaterialTextField name="select" select label="Level (optional)" value={level} onChange={(e) => setLevel(e.target.value as CourseLevel | '')}>
                <MaterialFormOption value="">— Keep —</MaterialFormOption>
                <MaterialFormOption value={CourseLevel.Beginner}>Beginner</MaterialFormOption>
                <MaterialFormOption value={CourseLevel.Intermediate}>Intermediate</MaterialFormOption>
                <MaterialFormOption value={CourseLevel.Advanced}>Advanced</MaterialFormOption>
            </MaterialTextField>
            <MaterialTextField
                name="select"
                select
                label="Subscription Plan (optional)"
                value={subscriptionPlan}
                onChange={(e) => setSubscriptionPlan(e.target.value as SubscriptionPlans | '')}
            >
                <MaterialFormOption value="">— Keep —</MaterialFormOption>
                <MaterialFormOption value={SubscriptionPlans.Free}>{SubscriptionPlans.Free}</MaterialFormOption>
                <MaterialFormOption value={SubscriptionPlans.Premium}>{SubscriptionPlans.Premium}</MaterialFormOption>
            </MaterialTextField>
            <div style={{ display: 'flex', gap: 16, marginTop: 12, marginBottom: 12 }}>
                <CheckBoxField
                    name="isNew"
                    checked={isNew}
                    onChange={() => {
                        setIsNew(!isNew);
                        setDirtyFlags((f) => ({ ...f, isNew: true }));
                    }}
                    label="New badge"
                />
                <CheckBoxField
                    name="isFeatured"
                    checked={isFeatured}
                    onChange={() => {
                        setIsFeatured(!isFeatured);
                        setDirtyFlags((f) => ({ ...f, isFeatured: true }));
                    }}
                    label="Featured"
                />
                <CheckBoxField
                    name="isFlagship"
                    checked={isFlagship}
                    onChange={() => {
                        setIsFlagship(!isFlagship);
                        setDirtyFlags((f) => ({ ...f, isFlagship: true }));
                    }}
                    label="Flagship"
                />
            </div>
            <MaterialTextField label="Cover Gradient CSS" value={coverGradient} onChange={(e) => setCoverGradient(e.target.value)} />
            <div>
                <p>Cover Image (optional override)</p>
                <DropZoneField
                    customHeight
                    accept="image/jpeg, image/jpg, image/png"
                    currentValue={imagePath}
                    currentFileType="image"
                    onChange={(p) => setImagePath(p)}
                />
            </div>
            <MaterialTextField
                label="Order Index (optional override)"
                type="number"
                value={orderIndex}
                onChange={(e) => setOrderIndex(e.target.value === '' ? '' : Number(e.target.value))}
            />
        </div>
    );
};

export default CoursePayloadForm;
